import {usePrefersReducedMotion} from '@dropbox/dig-foundations';
import {useLayoutEffect, useRef} from 'react';
import styles from 'views/workstreams/detail/WorkstreamDetailsShell.module.css';
import {SectionToggleButton} from 'views/workstreams/detail/WorkstreamSectionCard';

import {fadeIn} from './FadeIn';

export const MEMBERS_SECTION_ID = 'members-section';
// What the properties row's people chip flashes.
export const MEMBERS_LIST_ID = 'members-list';

/** Rings `id`'s element briefly; again on every call (restarts a running flash). */
export const flashElement = (id: string) => {
  const element = document.getElementById(id);
  if (!element) return;
  element.classList.remove(styles.flashTarget);
  void element.offsetWidth; // Restyle, so re-adding the class restarts the animation.
  element.classList.add(styles.flashTarget);
  element.addEventListener('animationend', () => element.classList.remove(styles.flashTarget), {
    once: true,
  });
};

/**
 * /workstreams detail: a collapsible "Members" row matching Strategy /
 * Activity. The list itself is the sidebar's people sections, portaled into
 * `onSlot`'s element — kept mounted (just hidden) while collapsed so they never
 * fall back into the properties row.
 */
export const MembersDisclosure = ({
  isExpanded,
  onExpandedChange,
  onSlot,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onSlot: (element: HTMLElement | null) => void;
}) => {
  const {hasReducedMotion} = usePrefersReducedMotion();
  // The list stays mounted, so it fades as it's shown: on opening, not when the
  // page arrives open.
  const wasExpanded = useRef(isExpanded);
  useLayoutEffect(() => {
    if (isExpanded && !wasExpanded.current && !hasReducedMotion) {
      fadeIn(document.getElementById(MEMBERS_LIST_ID));
    }
    wasExpanded.current = isExpanded;
  }, [isExpanded, hasReducedMotion]);

  return (
    <section className={styles.activitySection} id={MEMBERS_SECTION_ID}>
      <div
        // Steady, like Strategy / Activity here, which grow a control when open.
        className={[
          styles.activityHeader,
          styles.activityHeaderSteady,
          !isExpanded && styles.activityHeaderCollapsed,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <SectionToggleButton
          fold="members"
          title="Members"
          isExpanded={isExpanded}
          onToggle={() => onExpandedChange(!isExpanded)}
        />
      </div>
      {/* Hidden via style: the class sets display, which beats the `hidden` attribute. */}
      <div
        id={MEMBERS_LIST_ID}
        ref={onSlot}
        className={styles.sidebarMembers}
        style={isExpanded ? undefined : {display: 'none'}}
      />
    </section>
  );
};
